import {
  COMPARE_STORAGE_KEY,
  decodeHtmlEntities,
  getCompareHandles,
  MAX_COMPARE_ITEMS,
  readStoredItems,
} from "./compare-constants";
import { isScmQuoteOnlyTitle } from "../../scm-quote-only";

const STOREFRONT_API_URL = "/api/2025-01/graphql.json";

declare const routes: {
  cart_add_url?: string;
};

declare const publish: (eventName: string, payload: unknown) => void;

declare const PUB_SUB_EVENTS: {
  cartUpdate: string;
};

type CompareProduct = {
  id: string;
  title: string;
  handle: string;
  vendor: string;
  tags?: string[] | null;
  featuredImage?: {
    url?: string;
    altText?: string;
  };
  metafields?: {
    namespace?: string;
    key?: string;
    value?: string;
  }[];
  variants?: {
    edges: {
      node: {
        id: string;
        availableForSale: boolean;
        price?: {
          amount?: string;
          currencyCode?: string;
        };
        sku?: string;
        storeAvailability?: {
          edges: {
            node: {
              available: boolean;
              location: { name: string };
            };
          }[];
        };
      };
    }[];
  };
};

type VariantNode = NonNullable<
  CompareProduct["variants"]
>["edges"][number]["node"];

const isVariantAvailableForQfc = (node: VariantNode): boolean => {
  const edges = node.storeAvailability?.edges ?? [];
  if (edges.length === 0) return node.availableForSale;
  return edges.some(
    (edge) => edge.node.location.name === "QFC" && edge.node.available,
  );
};

type MetafieldIdentifier = { namespace: string; key: string };
type CompareStrings = {
  remove: string;
  addToCart: string;
  outOfStock: string;
  heading: string;
};

const compareButtonSpinner = `
  <div class="loading-overlay__spinner hidden">
    <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
      <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
    </svg>
  </div>
`;

const getMetafieldIdentifiers = (root: HTMLElement): MetafieldIdentifier[] => {
  const groupRows = root.querySelectorAll<HTMLTableRowElement>(
    "#compare-groups tr[data-metafield-namespace][data-metafield-key]",
  );
  const seen = new Set<string>();
  const identifiers: MetafieldIdentifier[] = [];
  for (const row of groupRows) {
    const namespace = (row.getAttribute("data-metafield-namespace") ?? "").trim();
    const key = (row.getAttribute("data-metafield-key") ?? "").trim();
    if (!namespace || !key) continue;
    const id = `${namespace}:${key}`;
    if (seen.has(id)) continue;
    seen.add(id);
    identifiers.push({ namespace, key });
  }
  return identifiers;
};

const FIELDS_BASE = `
  id
  title
  handle
  vendor
  tags
  featuredImage { url altText }
  variants(first: 20) {
    edges {
      node {
        id
        sku
        price { amount currencyCode }
        availableForSale
        storeAvailability(first: 10) {
          edges {
            node {
              available
              location { name }
            }
          }
        }
      }
    }
  }
`;

const FIELDS_METAFIELDS = `
  metafields(identifiers: $identifiers) {
    namespace
    key
    value
  }
`;

const buildQuery = (withMetafields: boolean): string => {
  const fields =
    FIELDS_BASE + (withMetafields ? "\n  " + FIELDS_METAFIELDS.trim() : "");
  return `
  query($h1: String!, $h2: String!, $h3: String!${withMetafields ? ", $identifiers: [HasMetafieldsIdentifier!]!" : ""}) {
    p1: product(handle: $h1) { ${fields} }
    p2: product(handle: $h2) { ${fields} }
    p3: product(handle: $h3) { ${fields} }
  }`;
};

const fetchProducts = (
  handles: string[],
  storefrontToken: string,
  metafieldIdentifiers: MetafieldIdentifier[],
): Promise<{ products: CompareProduct[]; error?: string }> => {
  if (!handles.length) return Promise.resolve({ products: [] });
  const [h1 = "", h2 = "", h3 = ""] = handles;
  const withMetafields = metafieldIdentifiers.length > 0;
  const variables: {
    h1: string;
    h2: string;
    h3: string;
    identifiers?: MetafieldIdentifier[];
  } = { h1, h2, h3 };
  if (withMetafields) variables.identifiers = metafieldIdentifiers;

  return fetch(STOREFRONT_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": storefrontToken,
    },
    body: JSON.stringify({
      query: buildQuery(withMetafields),
      variables,
    }),
  })
    .then((response) => response.json())
    .then(
      (res: {
        errors?: { message?: string }[];
        data?: {
          p1?: CompareProduct;
          p2?: CompareProduct;
          p3?: CompareProduct;
        };
      }) => {
        if (!res.data) {
          return {
            products: [],
            error: res.errors?.[0]?.message || "GraphQL error",
          };
        }
        const productList = [res.data.p1, res.data.p2, res.data.p3].filter(
          (product): product is CompareProduct => Boolean(product),
        );
        const products = handles
          .map((handle) =>
            productList.find((product) => product.handle === handle),
          )
          .filter((product): product is CompareProduct => Boolean(product));
        return { products, error: undefined };
      },
    )
    .catch((error: unknown) => ({
      products: [],
      error: error instanceof Error ? error.message : "Network error",
    }));
};

const getMetafieldValue = (
  product: CompareProduct,
  namespace: string,
  key: string,
): string => {
  const metafield = product.metafields?.find(
    (field) =>
      field != null && field.namespace === namespace && field.key === key,
  );
  return metafield?.value ? String(metafield.value).trim() : "";
};

const formatMoney = (amount?: string, currency?: string): string => {
  const root = document.getElementById("compare-root");
  const fmt = root?.getAttribute("data-money-format") || "";
  const value = Number(amount ?? "0") || 0;
  const lang = document.documentElement.lang || "en";

  const formatNumber = (
    minimumFractionDigits: number,
    maximumFractionDigits: number,
  ): string =>
    new Intl.NumberFormat(lang, {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(value);

  if (fmt.includes("{{amount_no_decimals}}")) {
    return fmt.replace("{{amount_no_decimals}}", formatNumber(0, 0));
  }

  if (fmt.includes("{{amount}}")) {
    return fmt.replace("{{amount}}", formatNumber(2, 2));
  }

  return new Intl.NumberFormat(lang, {
    style: "currency",
    currency: currency || "AUD",
  }).format(value);
};

const escapeHtml = (text: string): string => {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};

const escapeHtmlAttribute = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const areAllValuesEqual = (values: string[]): boolean =>
  values.length <= 1 || values.every((value) => value === values[0]);

type SimpleRowConfig = {
  type: string;
  getValue: (product: CompareProduct) => string;
};

const fillSimpleRows = (
  root: HTMLElement,
  products: CompareProduct[],
  rows: SimpleRowConfig[],
): void => {
  rows.forEach(({ type, getValue }) => {
    const tr = root.querySelector<HTMLElement>(
      `#compare-tbody tr[data-row-type="${type}"]`,
    );
    if (!tr) return;
    const cellValues: string[] = [];

    for (let i = 0; i < products.length; i++) {
      const td = tr.querySelector<HTMLElement>(`td[data-product-index="${i}"]`);
      if (!td) continue;
      const value = getValue(products[i]);
      td.textContent = value;
      cellValues.push(value);
    }

    tr.dataset.identical = String(areAllValuesEqual(cellValues));
  });
};

const showEmptyState = (root: HTMLElement, message: string): void => {
  const wrap = root.querySelector("#compare-table-wrap");
  const empty = root.querySelector("#compare-empty");
  if (!wrap || !empty) return;
  wrap.setAttribute("hidden", "");
  empty.removeAttribute("hidden");
  const title = empty.querySelector<HTMLElement>(".compare__empty-title");
  if (title) title.textContent = decodeHtmlEntities(message);
};

const fillCompareTable = (
  root: HTMLElement,
  products: CompareProduct[],
  labels: CompareStrings,
): void => {
  const tableWrap = root.querySelector<HTMLElement>("#compare-table-wrap");
  const emptyState = root.querySelector<HTMLElement>("#compare-empty");
  const heading = root.querySelector<HTMLElement>(".compare__heading");
  if (!tableWrap || !emptyState || !heading) return;

  if (!products.length) {
    return;
  }
  emptyState.setAttribute("hidden", "");
  tableWrap.removeAttribute("hidden");
  heading.textContent = labels.heading;

  const hideIdenticalCheckbox = document.getElementById(
    "compare-hide-identical",
  );
  if (hideIdenticalCheckbox instanceof HTMLInputElement) {
    if (!hideIdenticalCheckbox.dataset.compareHideIdenticalBound) {
      hideIdenticalCheckbox.addEventListener("change", () =>
        tableWrap.classList.toggle(
          "compare--hide-identical",
          hideIdenticalCheckbox.checked,
        ),
      );
      hideIdenticalCheckbox.dataset.compareHideIdenticalBound = "true";
    }
  }

  const toolbarVendorRow = root.querySelector<HTMLElement>(
    '#compare-sticky-bar tr[data-row-type="toolbar-vendor"]',
  );
  if (toolbarVendorRow) {
    for (let i = 0; i < MAX_COMPARE_ITEMS; i++) {
      const td = toolbarVendorRow.querySelector<HTMLElement>(
        `td[data-product-index="${i}"]`,
      );
      if (!td) continue;
      if (i >= products.length) {
        td.textContent = "—";
        continue;
      }
      const product = products[i];
      const vendorText = product.vendor;
      const titleText = product.title;
      const vendorHtml = vendorText
        ? `<p class="compare-table__vendor-title">${escapeHtml(vendorText)}</p>`
        : "";
      const titleHtml = titleText
        ? `<p class="compare-table__product-name">${escapeHtml(titleText)}</p>`
        : "";
      const removeHandle = product.handle;
      const safeHandleForData = escapeHtmlAttribute(removeHandle);
      const safeRemoveLabel = escapeHtmlAttribute(labels.remove);
      const removeButtonHtml = safeHandleForData
        ? `<button type="button" class="compare-table__remove compare-table__remove--icon" data-handle="${safeHandleForData}" aria-label="${safeRemoveLabel}">×</button>`
        : "";
      const vendorHeadHtml =
        vendorHtml || removeButtonHtml
          ? `<div class="compare-table__vendor-head">${vendorHtml}${removeButtonHtml}</div>`
          : "";
      const hasContent = vendorHeadHtml || titleHtml;
      td.innerHTML = hasContent
        ? `<div class="compare-table__vendor-row">${vendorHeadHtml}${titleHtml}</div>`
        : "—";
    }
  }

  for (let i = 0; i < MAX_COMPARE_ITEMS; i++) {
    const th = root.querySelector<HTMLElement>(
      `#compare-thead th[data-product-index="${i}"]`,
    );
    if (!th) continue;
    if (i >= products.length) {
      root
        .querySelectorAll<HTMLElement>(`[data-product-index="${i}"]`)
        .forEach((el) => el.classList.add("compare__col-hidden"));
      continue;
    }
    const product = products[i];
    const productHandle = product.handle;
    const productUrlPath = `/products/${encodeURIComponent(productHandle)}`;
    const rawImageUrl = product.featuredImage?.url || "";
    const normalizedImageUrl = rawImageUrl
      ? rawImageUrl.replace(/^\/\//, "https://")
      : "";
    const altText = product.featuredImage?.altText || "";
    const imageHtml = normalizedImageUrl
      ? `<img src="${escapeHtmlAttribute(
          normalizedImageUrl,
        )}" alt="${escapeHtmlAttribute(
          altText,
        )}" width="120" height="120" loading="lazy">`
      : "";
    const title = escapeHtml(product.title);
    th.innerHTML = `<div class="compare-table__product">
      <a href="${productUrlPath}" class="compare-table__img-wrap">${imageHtml}</a>
      <div class="compare-table__product-top">
        <div class="compare-table__product-top-text">
          <a href="${productUrlPath}" class="compare-table__title-link">${title}</a>
        </div>
      </div>
    </div>`;
  }

  const priceValueGetter = (p: CompareProduct): string => {
    const price = p.variants?.edges?.[0]?.node?.price;
    return price ? formatMoney(price.amount, price.currencyCode) : "—";
  };

  fillSimpleRows(root, products, [
    { type: "title", getValue: (p) => p.variants?.edges?.[0]?.node?.sku || "—" },
  ]);

  const footerPriceRow = root.querySelector<HTMLElement>(
    '#compare-table-footer tr[data-row-type="priceBottom"]',
  );
  if (footerPriceRow) {
    for (let i = 0; i < products.length; i++) {
      const td = footerPriceRow.querySelector<HTMLElement>(
        `td[data-product-index="${i}"]`,
      );
      if (!td) continue;
      td.textContent = priceValueGetter(products[i]);
    }
  }

  const footerActionsRow = root.querySelector<HTMLElement>(
    '#compare-table-footer tr[data-row-type="actions-bottom"]',
  );
  const actionRows = footerActionsRow ? [footerActionsRow] : [];
  if (actionRows.length > 0) {
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      const variantEdges = product.variants?.edges ?? [];
      const primaryNode =
        variantEdges.find((edge) => isVariantAvailableForQfc(edge.node))
          ?.node ??
        variantEdges[0]?.node ??
        null;
      const isScmQuoteOnly = isScmQuoteOnlyTitle(product.title);
      const canAddToCart =
        primaryNode !== null &&
        isVariantAvailableForQfc(primaryNode) &&
        !isScmQuoteOnly;
      const variantId = canAddToCart && primaryNode ? primaryNode.id : null;
      const variantSku = primaryNode?.sku ?? "";
      const isSpecialOrder = Array.isArray(product.tags)
        ? product.tags.includes("Special Order")
        : false;

      for (const row of actionRows) {
        const actionsCell = row.querySelector<HTMLElement>(
          `td[data-product-index="${i}"]`,
        );
        if (!actionsCell) continue;
        if (canAddToCart && variantId) {
          const skuAttr = variantSku ? ` data-variant-sku="${variantSku}"` : "";
          const specialOrderAttr = isSpecialOrder
            ? ' data-special-order="true"'
            : "";
          actionsCell.innerHTML = `<button type="button" class="compare-table__atc button button--primary" data-variant-id="${variantId}"${skuAttr}${specialOrderAttr}><span>${labels.addToCart}</span>${compareButtonSpinner}</button>`;
        } else {
          actionsCell.innerHTML = `<button type="button" class="compare-table__atc button button--primary" disabled><span>${labels.outOfStock}</span>${compareButtonSpinner}</button>`;
        }
      }
    }

    for (const row of actionRows) row.setAttribute("data-identical", "true");
  }

  const metafieldRows = root.querySelectorAll<HTMLTableRowElement>(
    "#compare-groups tr[data-metafield-namespace][data-metafield-key]",
  );
  metafieldRows.forEach((tr) => {
    const namespace = (
      tr.getAttribute("data-metafield-namespace") ?? ""
    ).trim();
    const key = (tr.getAttribute("data-metafield-key") ?? "").trim();
    if (!namespace || !key) return;

    const cellValues: string[] = [];
    for (let i = 0; i < products.length; i++) {
      const td = tr.querySelector<HTMLTableCellElement>(
        `td[data-product-index="${i}"]`,
      );
      if (!td) continue;
      const value = getMetafieldValue(products[i], namespace, key) || "—";
      td.textContent = value;
      cellValues.push(value);
    }
    if (cellValues.every((v) => v === "—")) {
      tr.setAttribute("hidden", "");
      return;
    }
    tr.removeAttribute("hidden");
    tr.setAttribute(
      "data-identical",
      areAllValuesEqual(cellValues) ? "true" : "false",
    );
  });

  const groups = root.querySelectorAll<HTMLElement>("#compare-groups .compare__group");
  for (const group of groups) {
    const rows = group.querySelectorAll<HTMLTableRowElement>("tr.compare-group-row");
    let hasValueRow = false;
    for (const row of rows) {
      if (!row.hasAttribute("hidden")) {
        hasValueRow = true;
        break;
      }
    }
    if (!hasValueRow) group.setAttribute("hidden", "");
    else group.removeAttribute("hidden");
  }

  for (let i = products.length; i < MAX_COMPARE_ITEMS; i++) {
    root
      .querySelectorAll<HTMLElement>(`[data-product-index="${i}"]`)
      .forEach((el) => el.remove());
  }
};

function renderCompareBreadcrumbs(container: HTMLElement): void {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("from_url") ?? "";
  const fromTitle = params.get("from_title") ?? "";

  if (!fromUrl.startsWith("/") || !fromTitle) return;

  const nav = container.querySelector<HTMLElement>("nav");
  const fromLink = container.querySelector<HTMLAnchorElement>(".compare-breadcrumbs__from");
  const fromSpan = fromLink?.querySelector("span");

  if (!nav || !fromLink || !fromSpan) return;

  fromLink.href = fromUrl;
  fromSpan.textContent = fromTitle;
  nav.removeAttribute("hidden");
}

function updateComparePageUrl(handles: string[], baseUrl: string): void {
  if (!("history" in window)) return;
  try {
    const url = new URL(baseUrl, window.location.origin);
    const existingParams = new URLSearchParams(window.location.search);
    const fromUrl = existingParams.get("from_url");
    const fromTitle = existingParams.get("from_title");
    url.search = "";
    for (const handle of handles.slice(0, MAX_COMPARE_ITEMS)) {
      url.searchParams.append("handle", handle);
    }
    if (fromUrl) url.searchParams.set("from_url", fromUrl);
    if (fromTitle) url.searchParams.set("from_title", fromTitle);
    window.history.replaceState(null, "", url.toString());
  } catch (_error) {
    console.error("[compare-page] Failed to update compare URL", _error);
  }
}

function renderCompare(root: HTMLElement): void {
  const breadcrumbsContainer = document.getElementById("compare-breadcrumbs");
  if (breadcrumbsContainer) renderCompareBreadcrumbs(breadcrumbsContainer);

  const productHandles = getCompareHandles();
  const baseUrl =
    root.getAttribute("data-compare-base-url") ||
    location.pathname ||
    "/pages/compare";

  updateComparePageUrl(productHandles, baseUrl);

  const storefrontToken = (
    root.getAttribute("data-storefront-token") || ""
  ).trim();

  if (productHandles.length && !storefrontToken) {
    return showEmptyState(
      root,
      root.getAttribute("data-compare-token-required") ||
        "Add Storefront API token.",
    );
  }
  if (!productHandles.length) {
    return showEmptyState(
      root,
      root.getAttribute("data-compare-empty") || "Add products to compare.",
    );
  }

  const getTextAttr = (attr: string, fallback: string): string =>
    decodeHtmlEntities(root.getAttribute(attr) || fallback);

  const labels: CompareStrings = {
    remove: getTextAttr("data-compare-remove", "Remove"),
    addToCart: getTextAttr("data-compare-add-to-cart", "Add to cart"),
    outOfStock: getTextAttr("data-compare-out-of-stock", "Sold out"),
    heading: getTextAttr("data-compare-heading", "Compare products"),
  };

  fetchProducts(
    productHandles,
    storefrontToken,
    getMetafieldIdentifiers(root),
  ).then((res) => {
    if (res.error) return showEmptyState(root, res.error);
    if (!res.products.length)
      return showEmptyState(
        root,
        root.getAttribute("data-compare-load-failed") ||
          "Could not load products.",
      );
    fillCompareTable(root, res.products, labels);
  });
}

let comparePageInitialized = false;

const handleCompareAtcClick = async (event: MouseEvent): Promise<void> => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest(".compare-table__atc");
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;
  const variantIdAttr = button.getAttribute("data-variant-id");
  if (!variantIdAttr) return;
  const variantIdNumeric = variantIdAttr.startsWith("gid://")
    ? (variantIdAttr.split("/").pop() ?? variantIdAttr)
    : variantIdAttr;
  button.disabled = true;
  button.classList.add("loading");
  button.querySelector(".loading-overlay__spinner")?.classList.remove("hidden");

  const cartElement =
    document.querySelector("cart-notification") ||
    document.querySelector("cart-drawer");
  const addUrl =
    typeof routes !== "undefined" && routes?.cart_add_url
      ? routes.cart_add_url
      : "/cart/add.js";

  const config: RequestInit & { headers: Record<string, string> } = {
    method: "POST",
    headers: {
      Accept: "application/javascript",
      "X-Requested-With": "XMLHttpRequest",
    },
  };
  const formData = new FormData();
  formData.append("id", variantIdNumeric);
  formData.append("quantity", "1");

  const root = document.getElementById("compare-root");
  const skuLabel =
    root?.getAttribute("data-compare-sku-label") || "SKU";

  const variantSkuAttr = button.getAttribute("data-variant-sku") || "";
  const variantSku = variantSkuAttr.trim();
  if (variantSku && skuLabel) {
    formData.append(`properties[${skuLabel}]`, variantSku);
  }

  const isSpecialOrder = button.getAttribute("data-special-order") === "true";
  if (isSpecialOrder) {
    formData.append("properties[_special_order]", "true");
  }

  const cartApi =
    cartElement && typeof cartElement === "object" ? cartElement : null;

  const getSectionsToRender: unknown = cartApi
    ? Reflect.get(cartApi, "getSectionsToRender")
    : undefined;
  const setActiveElement: unknown = cartApi
    ? Reflect.get(cartApi, "setActiveElement")
    : undefined;
  const renderContents: unknown = cartApi
    ? Reflect.get(cartApi, "renderContents")
    : undefined;

  if (typeof getSectionsToRender === "function") {
    const sections: unknown = getSectionsToRender.call(cartApi);
    if (Array.isArray(sections) && sections.length) {
      const sectionIds = sections
        .map((section) => {
          if (typeof section !== "object" || section === null) return "";
          const id: unknown = Reflect.get(section, "id");
          return typeof id === "string" ? id : "";
        })
        .filter(Boolean)
        .join(",");
      if (sectionIds) formData.append("sections", sectionIds);
    }
    formData.append("sections_url", window.location.pathname);
    if (typeof setActiveElement === "function") {
      setActiveElement.call(cartApi, document.activeElement);
    }
  }

  config.body = formData;

  let shouldReenable = true;
  try {
    const response = await fetch(addUrl, config);
    const parsed: unknown = await response.json().catch(() => ({}));
    const payloadStatusValue: unknown =
      typeof parsed === "object" && parsed !== null
        ? Reflect.get(parsed, "status")
        : undefined;
    const payloadStatus =
      typeof payloadStatusValue === "number" ? payloadStatusValue : undefined;
    if (!response.ok || (payloadStatus && payloadStatus >= 400)) return;

    shouldReenable = false;
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: "compare-page",
      productVariantId: variantIdNumeric,
      cartData: parsed,
    });
    if (typeof renderContents === "function")
      renderContents.call(cartApi, parsed);
  } catch (error: unknown) {
    console.error("[compare-page] Failed to add to cart", error);
  } finally {
    button.classList.remove("loading");
    button.querySelector(".loading-overlay__spinner")?.classList.add("hidden");
    if (shouldReenable) button.disabled = false;
  }
};

const handleCompareRemoveClick = (event: MouseEvent): void => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest(".compare-table__remove");
  if (!button) return;
  const handle = button.getAttribute("data-handle") ?? "";
  if (!handle) return;
  event.preventDefault();
  try {
    const updated = readStoredItems().filter(
      (item) => /\/products\/([^/?]+)/.exec(item.url)?.[1] !== handle,
    );
    localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent("carbatec:compare-changed"));
  } catch (error) {
    console.error(
      "[compare-page] Failed to update compare list on remove",
      error,
    );
  }
};

const handleGroupSummaryClick = (event: MouseEvent): void => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const summaryRow = target.closest(".compare__group-summary");
  if (!summaryRow) return;
  const group = summaryRow.closest(".compare__group");
  if (!(group instanceof HTMLElement)) return;
  const isCollapsed = group.dataset.collapsed === "true";
  group.dataset.collapsed = isCollapsed ? "false" : "true";
};

const bindCompareAtc = (root: HTMLElement): void => {
  if (root.dataset.compareAtcBound === "true") return;
  root.dataset.compareAtcBound = "true";
  root.addEventListener("click", handleCompareAtcClick);
  root.addEventListener("click", handleCompareRemoveClick);
  root.addEventListener("click", handleGroupSummaryClick);
};

const initCompareStickyBar = (root: HTMLElement): void => {
  if (root.dataset.compareStickyBarBound === "true") return;
  root.dataset.compareStickyBarBound = "true";

  const scrollInner = root.querySelector<HTMLElement>(
    ".compare__table-scroll-inner",
  );
  const stickyBar = root.querySelector<HTMLElement>("#compare-sticky-bar");
  const stickyFooter = root.querySelector<HTMLElement>("#compare-sticky-footer");
  if (!scrollInner || !stickyBar) return;

  const stickyTargets = stickyFooter ? [stickyBar, stickyFooter] : [stickyBar];

  let isSyncing = false;

  const syncScrollLeft = (
    source: HTMLElement,
    targets: HTMLElement[],
  ): void => {
    if (isSyncing) return;
    const left = source.scrollLeft;
    isSyncing = true;
    for (const target of targets) {
      if (target.scrollLeft === left) continue;
      target.scrollLeft = left;
    }
    requestAnimationFrame(() => {
      isSyncing = false;
    });
  };

  scrollInner.addEventListener(
    "scroll",
    () => syncScrollLeft(scrollInner, stickyTargets),
    { passive: true },
  );

  syncScrollLeft(scrollInner, stickyTargets);
};

const initComparePage = (): void => {
  const root = document.getElementById("compare-root");
  if (!root) return;
  bindCompareAtc(root);
  renderCompare(root);
  initCompareStickyBar(root);
  if (!comparePageInitialized) {
    const rerender = (): void => {
      const currentRoot = document.getElementById("compare-root");
      if (currentRoot) renderCompare(currentRoot);
    };
    window.addEventListener("carbatec:compare-changed", rerender);
    window.addEventListener("storage", (event) => {
      if (event.key === COMPARE_STORAGE_KEY) rerender();
    });
    comparePageInitialized = true;
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initComparePage);
} else {
  initComparePage();
}
document.addEventListener("shopify:section:load", initComparePage);
